/**
 * 파일명 : evl0104m.tsx
 * 설명 : 인사평가 평가항목조회화면
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
import React, { useMemo, useEffect, useCallback, useState, useRef } from "react";
/*  AntD  */
import {
  Layout,
  Typography,
  Space,
  Form,
  Select,
  Input,
  Button,
  Table,
  message,
  Checkbox,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";

/*  업무  */
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";

/*  dayjs  */
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
/* Reducer */
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import type { IEvl0104mRow, SelectOption } from "redux/models/evl/evl0104m_model";
import { Evl0104mAction } from "../../../redux/slice/evl/evl0104m_slice";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);


/************************************************
 * 코드 트리 (검색 Select용)
 * - 평가구분(evlCd) -> 항목유형(itmTypCd) -> 상세유형(dtlTypCd)
 * - 검색박스 옵션은 DB 공통코드(060/061/062) 대신 이 관계를 따른다.
 ************************************************/
type CodeNode = {
  value: string;
  label: string;
  children?: CodeNode[];
};

  const getScreenId = (type: string) => {
    switch (type) {
      case "Pledge":
        return "evl0102m";

      case "HR-Junior":
      case "HR-Senior":
        return "evl0105m";

      case "Pjt-Mem":
        return "evl0106m";

      case "Pjt-PM":
        return "evl0107m";

      default:
        return "evl0104m";
    }
  };

 const openPreviewPopupByType = (type: string) => {
    const ScreenId = getScreenId(type);

    const params: any = {
      title: `미리보기`,
      width: '68%',
      height: 720,
      popupId: ScreenId,
      props: {
        evlType: type, // 화면에서 어떤 양식인지 식별용
        viewMode: "preview",
      },
    };

    common.openPopup(params);
  };

const CODE_TREE: CodeNode[] = [
  {
    value: "01",
    label: "인사평가 - 책임이상",
    children: [
      {
        value: "01",
        label: "업적",
        children: [
          { value: "01", label: "업적평가" },
          { value: "02", label: "업적기술" },
        ],
      },
      {
        value: "02",
        label: "역량",
        children: [
          { value: "03", label: "공통역량" },
          { value: "04", label: "역할역량" },
          { value: "05", label: "직무역량" },
        ],
      },
      {
        value: "03",
        label: "능력개발 및 업무개선사항",
        children: [
          { value: "06", label: "IT직무능력" },
          { value: "07", label: "업무직무능력" },
          { value: "08", label: "자기계발" },
          { value: "09", label: "업무개선" },
          { value: "10", label: "부하직원육성" },
          { value: "11", label: "기타" },
        ],
      },
    ],
  },
  {
    value: "02",
    label: "인사평가 - 선임이하",
    children: [
      {
        value: "01",
        label: "업적",
        children: [
          { value: "01", label: "업적평가" },
          { value: "02", label: "업적기술" },
        ],
      },
      {
        value: "02",
        label: "역량",
        children: [
          { value: "03", label: "공통역량" },
          { value: "04", label: "역할역량" },
          { value: "05", label: "직무역량" },
        ],
      },
      {
        value: "03",
        label: "능력개발 및 업무개선사항",
        children: [
          { value: "06", label: "IT직무능력" },
          { value: "07", label: "업무직무능력" },
          { value: "08", label: "자기계발" },
          { value: "09", label: "업무개선" },
          { value: "10", label: "부하직원육성" },
          { value: "11", label: "기타" },
        ],
      },
    ],
  },
  {
    value: "03",
    label: "프로젝트평가 - PM",
    children: [
      {
        value: "04",
        label: "프로젝트 수행 내역",
        children: [{ value: "12", label: "프로젝트수행내역" }],
      },
      {
        value: "05",
        label: "프로젝트 수행 실적",
        children: [
          { value: "11", label: "기타" },
          { value: "13", label: "일정" },
          { value: "14", label: "인력" },
          { value: "15", label: "품질" },
          { value: "16", label: "비용" },
        ],
      },
    ],
  },
  {
    value: "04",
    label: "프로젝트평가 - 팀원",
    children: [
      {
        value: "04",
        label: "프로젝트 수행 내역",
        children: [{ value: "12", label: "프로젝트수행내역" }],
      },
      {
        value: "05",
        label: "프로젝트 수행 실적",
        children: [
          { value: "17", label: "공통평가" },
          { value: "18", label: "M/M" },
          { value: "19", label: "팀원" },
          { value: "20", label: "컨설턴트" },
        ],
      },
    ],
  },
  {
    value: "05",
    label: "업무서약서",
    children: [
      {
        value: "07",
        label: "기타서약",
        children: [
          { value: "22", label: "팀기여부문" },
          { value: "23", label: "자기개발부문" },
        ],
      },
    ],
  },
];

/************************************************
 * 전역변수 영역
 ************************************************/

const { Content } = Layout;
/**
 * 기능 : mapFromApiToUI
 * 설명 :
 *  - 서버(API)에서 내려오는 데이터를 화면에서 사용하는 IEvl0104mRow 형태로 변환한다.
 *  - 컬럼명이 camelCase / SNAKE_CASE / 대문자로 섞여 내려와도 하나의 규칙으로 통일한다.
 *  - 화면과 Redux에서는 항상 동일한 데이터 구조를 사용하도록 표준화한다.
 *  - 숫자형 필드(itmSeq)는 문자열, null, 빈값을 안전하게 숫자 또는 null로 변환한다.
 * param :
 *  - row : 서버에서 내려온 원본 데이터 1건
 * return :
 *  - IEvl0104mRow 타입으로 변환된 평가항목 Row 데이터
 **/
function mapFromApiToUI(row: any): IEvl0104mRow {
  return {
    evlCd: row.evlCd ?? row.EVL_CD ?? "",
    itmTypCd: row.itmTypCd ?? row.ITM_TYP_CD ?? "",
    dtlTypCd: row.dtlTypCd ?? row.DTL_TYP_CD ?? "",

    useStDt: row.useStDt ?? row.USE_ST_DT ?? "",
    useEdDt: row.useEdDt ?? row.USE_ED_DT ?? "",

    hrEvlItmId: row.hrEvlItmId ?? row.HR_EVL_ITM_ID ?? "",
    itmNm: row.itmNm ?? row.ITM_NM ?? "",
    itmDesc: row.itmDesc ?? row.ITM_DESC ?? "",

    itmSeq:
      (row.itmSeq ?? row.ITM_SEQ ?? row.itm_seq) !== undefined &&
      (row.itmSeq ?? row.ITM_SEQ ?? row.itm_seq) !== null &&
      String(row.itmSeq ?? row.ITM_SEQ ?? row.itm_seq).trim() !== ""
        ? Number(row.itmSeq ?? row.ITM_SEQ ?? row.itm_seq)
        : null,

    baseAsgn: row.baseAsgn ?? row.BASE_ASGN ?? "0",

    lstChgEmpNo: row.lstChgEmpNo ?? row.LST_CHG_EMP_NO ?? "",
    lstChgDtm: row.lstChgDtm ?? row.LST_CHG_DTM ?? "",
    lstChgGuid: row.lstChgGuid ?? row.LST_CHG_GUID ?? "",
  };
}

export default function Evl0104m() {
  const dispatch = useAppDispatch();

  // ✅ Com0101m 패턴: state.<SliceKey> (너희는 대문자 키)
  const items = useAppSelector((state) => state.Evl0104m.items);
  const filters = useAppSelector((state) => state.Evl0104m.filters);
  const evlCdOptions = useAppSelector((state) => state.Evl0104m.evlCdOptions);
  const itmTypCdOptions = useAppSelector((state) => state.Evl0104m.itmTypCdOptions);
  const dtlTypCdOptions = useAppSelector((state) => state.Evl0104m.dtlTypCdOptions);
  const loading = useAppSelector((state) => state.Evl0104m.loading);

  const [pageNo, setPageNo] = useState(1);


  const [filterForm] = Form.useForm();
  /************************************************
   * 검색 Select: CODE_TREE 관계 적용
   ************************************************/
  const watchEvlCd = Form.useWatch("evlCd", filterForm);
  const watchItmTypCd = Form.useWatch("itmTypCd", filterForm);

  const toTreeOptions = useCallback((nodes?: CodeNode[]): SelectOption[] => {
    const arr: SelectOption[] = [{ label: "전체", value: "" }];
    (nodes ?? []).forEach((n) => arr.push({ label: n.label, value: n.value }));
    return arr;
  }, []);

  const evlCdTreeOptions = useMemo(() => toTreeOptions(CODE_TREE), [toTreeOptions]);

  const itmTypTreeOptions = useMemo(() => {
    const evl = CODE_TREE.find((n) => n.value === String(watchEvlCd ?? ""));
    return toTreeOptions(evl?.children);
  }, [watchEvlCd, toTreeOptions]);

  const dtlTypTreeOptions = useMemo(() => {
    const evl = CODE_TREE.find((n) => n.value === String(watchEvlCd ?? ""));
    const itm = evl?.children?.find((n) => n.value === String(watchItmTypCd ?? ""));
    return toTreeOptions(itm?.children);
  }, [watchEvlCd, watchItmTypCd, toTreeOptions]);

  // 부모값이 바뀌면 자식값 초기화 (초기 1회는 유지)
  const didMountEvlRef = useRef(false);
  useEffect(() => {
    if (!didMountEvlRef.current) {
      didMountEvlRef.current = true;
      return;
    }
    filterForm.setFieldsValue({itmTypCd: "", dtlTypCd: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchEvlCd]);

  const didMountItmRef = useRef(false);
  useEffect(() => {
    if (!didMountItmRef.current) {
      didMountItmRef.current = true;
      return;
    }
    filterForm.setFieldsValue({ dtlTypCd: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchItmTypCd]);

  /**
   * 기능 : buildOptions
   * 설명 :
   *  - 공통코드 조회 결과를 Ant Design Select 컴포넌트에서 사용할 수 있는
   *    { label, value } 형태의 옵션 배열로 변환한다.
   *  - 항상 맨 앞에 "전체" 옵션을 자동으로 추가한다.
   *  - 평가구분, 항목유형, 상세유형 Select 박스에서 공통으로 사용된다.
   * param :
   *  - datas : 공통코드 API에서 내려온 코드 리스트
   * return :
   *  - SelectOption[] 형태의 옵션 배열
   **/
  const buildOptions = useCallback((datas: any[]): SelectOption[] => {
    const arr: SelectOption[] = [{ label: "전체", value: "" }];
    datas?.forEach((item: any) => {
      arr.push({
        label: item.cdNm ?? item.CD_NM ?? "",
        value: item.cd ?? item.CD ?? "",
      });
    });
    return arr;
  }, []);

  const setEvlCdList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) =>
        dispatch(Evl0104mAction.setOptions({ evlCdOptions: buildOptions(datas) })),
    };
    common.getComCodeList("060", option);
  }, [buildOptions, dispatch]);

  const setItmTypCdList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) =>
        dispatch(Evl0104mAction.setOptions({ itmTypCdOptions: buildOptions(datas) })),
    };
    common.getComCodeList("061", option);
  }, [buildOptions, dispatch]);

  const setDtlTypCdList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) =>
        dispatch(Evl0104mAction.setOptions({ dtlTypCdOptions: buildOptions(datas) })),
    };
    common.getComCodeList("062", option);
  }, [buildOptions, dispatch]);

  const evlCdLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    evlCdOptions.forEach((o: SelectOption) => {
      if (o.value !== "") m.set(o.value, o.label);
    });
    return m;
  }, [evlCdOptions]);

  const itmTypCdLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    itmTypCdOptions.forEach((o: SelectOption) => {
      if (o.value !== "") m.set(o.value, o.label);
    });
    return m;
  }, [itmTypCdOptions]);

  const dtlTypCdLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    dtlTypCdOptions.forEach((o: SelectOption) => {
      if (o.value !== "") m.set(o.value, o.label);
    });
    return m;
  }, [dtlTypCdOptions]);

  const fetchList = useCallback(
    async (req?: any) => {
      // ✅ 체크 안 하면 useYn을 아예 보내지 않도록(undefined)
      const requestData =
        req ??
        ({
          evlCd: filters.evlCd || "",
          itmTypCd: filters.itmTypCd || "",
          dtlTypCd: filters.dtlTypCd || "",
          useYn: filters.useYn ? "Y" : undefined,
          keyWord: (filters.keyWord || "").trim(),
        } as any);

      dispatch(Evl0104mAction.setLoading(true));
      try {
        await commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLR0006",
          success: (responseData: any) => {
            const rawList = responseData?.EvlItems;
            const mapped = Array.isArray(rawList) ? rawList.map(mapFromApiToUI) : [];
            dispatch(Evl0104mAction.setItems(mapped));
            // 🔥 여기만 조건 분기
            if (mapped.length === 0) {
              message.info("조회된 데이터가 없습니다.");
            } else {
              message.success(`조회 완료 (${mapped.length}건)`);
            }
          },
        });
      } finally {
        dispatch(Evl0104mAction.setLoading(false));
      }
    },
    [dispatch, filters]
  );

  // ✅ 최초 로드: 옵션 로드 + form 동기화 + 조회
  useEffect(() => {
    setEvlCdList();
    setItmTypCdList();
    setDtlTypCdList();

    // ✅ 진입 시 필터 초기화
    const initFilters = {
      evlCd: "",
      itmTypCd: "",
      dtlTypCd: "",
      useYn: false,
      keyWord: "",
    };

    dispatch(Evl0104mAction.resetFilters());
    filterForm.resetFields();
    filterForm.setFieldsValue(initFilters);

    // ✅ 페이지도 1로
    setPageNo(1);

    // ✅ redux 반영 타이밍 이슈 피하려고 req를 직접 넘겨서 전체조회
    fetchList({
      evlCd: "",
      itmTypCd: "",
      dtlTypCd: "",
      useYn: undefined, // fetchList 내부 규칙과 동일: 조건 없음
      keyWord: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredItems = useMemo(() => {
    const evalTp = (filters.evlCd || "").trim();
    const itemTp = (filters.itmTypCd || "").trim();
    const detailTp = (filters.dtlTypCd || "").trim();
    const keyWord = (filters.keyWord || "").trim();

    return (items as IEvl0104mRow[]).filter((it) => {
      if (evalTp && it.evlCd !== evalTp) return false;
      if (itemTp && it.itmTypCd !== itemTp) return false;
      if (detailTp && it.dtlTypCd !== detailTp) return false;

      if (keyWord) {
        const hay = `${it.hrEvlItmId} ${it.itmNm} ${it.itmDesc}`.toLowerCase();
        if (!hay.includes(keyWord.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, filters]);

  const openEvl0104p = (mode: "NEW" | "EDIT", row?: IEvl0104mRow) => {
    const accept = (_data: any) => fetchList();

    const params: any = {
      title: mode === "NEW" ? "평가 항목 신규" : "평가 항목 수정",
      width: 680,
      height: 520,
      popupId: "evl0104p",
      accept,
      props: {
        mode,
        hrEvlItmId: row?.hrEvlItmId ?? "",
        rowData: row ?? null,
      },
    };

    common.openPopup(params);
  };
  /**
   * 기능 : resetFilter
   * 설명 :
   *  - 모든 필터 조건을 초기값으로 되돌린다.
   *  - Redux 필터 상태와 Form 값을 동시에 초기화한다.
   *  - 초기화 후 전체 목록을 다시 조회한다.
   * param :
   *  - None
   * return :
   *  - None
   **/
  const resetFilter = async () => {
    const nextFilters = { evlCd: "", itmTypCd: "", dtlTypCd: "", useYn: false, keyWord: "" };

    dispatch(Evl0104mAction.resetFilters());
    filterForm.resetFields();
    filterForm.setFieldsValue(nextFilters);

    // ✅ 핵심: 테이블 페이지 1로
    setPageNo(1);

    // ✅ reset 시 useYn 아예 미전송(조건 없음)
    await fetchList({
      evlCd: "",
      itmTypCd: "",
      dtlTypCd: "",
      useYn: undefined,
      keyWord: "",
    });
  };
  /**
   * 기능 : applyFilter
   * 설명 :
   *  - 필터 Form에 입력된 값을 Redux 필터 상태에 반영한다.
   *  - 필터 조건을 기준으로 서버에 목록 조회를 요청한다.
   *  - 실제 검색 버튼 클릭 시 실행되는 핵심 필터 적용 함수이다.
   * param :
   *  - None
   * return :
   *  - None
   **/
  const applyFilter = async () => {
    const v = filterForm.getFieldsValue();

    const nextFilters = {
      evlCd: v.evlCd ?? "",
      itmTypCd: v.itmTypCd ?? "",
      dtlTypCd: v.dtlTypCd ?? "",
      useYn: !!v.useYn,
      keyWord: v.keyWord ?? "",
    };

    setPageNo(1);

    // ✅ 여기서만 Redux에 반영(= 커밋)
    dispatch(Evl0104mAction.setFilters(nextFilters));

    // ✅ 체크된 경우만 useYn=Y 전송, 아니면 미전송(undefined)
    await fetchList({
      evlCd: nextFilters.evlCd,
      itmTypCd: nextFilters.itmTypCd,
      dtlTypCd: nextFilters.dtlTypCd,
      useYn: nextFilters.useYn ? "Y" : undefined,
      keyWord: (nextFilters.keyWord || "").trim(),
    });
  };

  const columns: ColumnsType<IEvl0104mRow> = [
    {
      title: "평가구분",
      dataIndex: "evlCd",
      width: 140,
      align: "center",
      render: (v: string) => evlCdLabelMap.get(v) ?? v,
    },
    {
      title: "항목유형",
      dataIndex: "itmTypCd",
      width: 160,
      align: "center",
      render: (v: string) => itmTypCdLabelMap.get(v) ?? v,
    },
    {
      title: "상세유형",
      dataIndex: "dtlTypCd",
      width: 180,
      align: "center",
      render: (v: string) => dtlTypCdLabelMap.get(v) ?? v,
    },
    { title: "항목명", dataIndex: "itmNm", width: 180, align: "center" },
    {
      title: <div style={{ textAlign: "center" }}>설명</div>,
      dataIndex: "itmDesc",
      width: 280,
      align: "left",
    },
    {
      title: "기본배점",
      dataIndex: "baseAsgn",
      width: 80,
      align: "center",
      render: (v: any) => {
        if (v === null || v === undefined) return "";
        if (String(v) === "0") return "";
        return v;
      },
    },
    {
      title: "상태",
      key: "status",
      width: 80,
      align: "center",
      render: (_, r) => {
        const now = dayjs();
        const start = dayjs(r.useStDt);
        const end = dayjs(r.useEdDt);
        const isUsing = start.isSameOrBefore(now) && end.isSameOrAfter(now);
        return <span className="status-text">{isUsing ? "사용" : "종료"}</span>;
      },
    },
    {
      title: "작업",
      key: "action",
      width: 120,
      align: "center",
      render: (_, r) => (
        <Space size={8}>
          <Button type="link" size="small" onClick={() => openEvl0104p("EDIT", r)}>
            수정
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="evaluation-layout">
      <Content className="evaluation-content">
        <Space direction="vertical" size={16} className="content-wrapper">
          <div className="page-header">
            <Typography.Title level={2} className="page-title" style={{ textAlign: "center" }}>
              평가 항목 관리
            </Typography.Title>
          </div>
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Space>
                <p>미리보기</p>
                <Button onClick={() => openPreviewPopupByType("Pledge")}>
                  업무서약서
                </Button>
                <Button onClick={() => openPreviewPopupByType("HR-Junior")}>
                  인사평가-선임이하
                </Button>

                <Button onClick={() => openPreviewPopupByType("HR-Senior")}>
                  인사평가-책임이상
                </Button>

                <Button onClick={() => openPreviewPopupByType("Pjt-Mem")}>
                  프로젝트평가-팀원
                </Button>

                <Button onClick={() => openPreviewPopupByType("Pjt-PM")}>
                  프로젝트평가-PM
                </Button>
              </Space>
            </div>

          <div
            className="filter-section"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 12,
            }}
          >
            {/* 좌측: 필터 영역 */}
            <div>
              <Form form={filterForm} layout="inline" initialValues={filters}>
                <Form.Item name="evlCd" label="평가구분">
                  <Select allowClear placeholder="선택" style={{ width: 160 }} options={evlCdTreeOptions} />
                </Form.Item>

                <Form.Item name="itmTypCd" label="항목유형">
                  <Select
                    allowClear
                    placeholder="선택"
                    style={{ width: 180 }}
                    options={itmTypTreeOptions}
                    disabled={!watchEvlCd}
                  />
                </Form.Item>

                <Form.Item name="dtlTypCd" label="상세유형">
                  <Select
                    allowClear
                    placeholder="선택"
                    style={{ width: 140 }}
                    options={dtlTypTreeOptions}
                    disabled={!watchEvlCd || !watchItmTypCd}
                  />
                </Form.Item>

                <Form.Item>
                  <Form.Item name="useYn" valuePropName="checked" noStyle>
                    <Checkbox>사용중인 항목만</Checkbox>
                  </Form.Item>
                </Form.Item>

                <Form.Item name="keyWord" label="검색">
                  <Input allowClear placeholder="항목명/설명" style={{ width: 220 }} />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button type="primary" onClick={applyFilter}>
                      검색
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={resetFilter}>
                      초기화
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>

            {/* 우측: 신규 등록 버튼 */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openEvl0104p("NEW")}
            >
              신규 등록
            </Button>
          </div>

          <div className="table-section">
            
            <Table
              rowKey="hrEvlItmId"
              columns={columns}
              dataSource={filteredItems}
              bordered
              size="middle"
              tableLayout="fixed"
              loading={loading}
              pagination={{
                current: pageNo,
                pageSize: 10,
                showSizeChanger: false,
                onChange: (page) => setPageNo(page),
              }}
            />
          </div>
        </Space>
      </Content>
    </Layout>
  );
}
